// src/components/IconButtonSet.js
import React from "react";
import { Button, IconButton as IcButton } from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretLeft,
  faCaretRight,
  faCheck,
  faCircleInfo,
  faDownload,
  faEraser,
  faEye,
  faFileExcel,
  faFilePdf,
  faFileWord,
  faKey,
  faMagnifyingGlass,
  faPaperPlane,
  faPenToSquare,
  faPlus,
  faPrint,
  faSave,
  faSquareCheck,
  faTrashCan,
  faUpload,
  faXmark,
  faDoorClosed
} from "@fortawesome/free-solid-svg-icons";
import { ButtonSetText } from "./Text";

// Component หลักสำหรับปุ่มที่มีข้อความ
const IconButton = ({
  label: customLabel,
  icon,
  colorScheme = "primary",
  variant = "solid",
  size = "md",
  ...props
}) => {
  return (
    <Button
      colorScheme={colorScheme}
      leftIcon={<FontAwesomeIcon icon={icon} />}
      variant={variant}
      size={size}
      {...props}
    >
      {customLabel}
    </Button>
  );
};

// Component สำหรับปุ่มที่มีแค่ไอคอน
const ShortIconButton = ({
  icon,
  colorScheme = "primary",
  size = "md",
  ...props
}) => {
  return (
    <IcButton
      colorScheme={colorScheme}
      icon={<FontAwesomeIcon icon={icon} />}
      size={size}
      {...props}
    />
  );
};

// กำหนดปุ่มทั้งหมดในรูปแบบ Object
const buttonConfigs = {
  Search: {
    icon: faMagnifyingGlass,
    colorScheme: "searchScheme",
    defaultLabel: ButtonSetText.search,
  },
  Login: {
    icon: faKey,
    colorScheme: "loginScheme",
    defaultLabel: ButtonSetText.login,
  },
  Approved: {
    icon: faCheck,
    colorScheme: "confirmScheme",
    defaultLabel: ButtonSetText.approved,
  },
  Verified: {
    icon: faCheck,
    colorScheme: "confirmScheme",
    defaultLabel: ButtonSetText.verified,
  },
  Reject: {
    icon: faXmark,
    colorScheme: "cancelScheme",
    defaultLabel: ButtonSetText.rejected,
  },
  NotApproved: {
    icon: faXmark,
    colorScheme: "cancelScheme",
    defaultLabel: ButtonSetText.notapproved,
  },
  Confirm: {
    icon: faCheck,
    colorScheme: "confirmScheme",
    defaultLabel: ButtonSetText.confirm,
  },
  Cancel: {
    icon: faXmark,
    colorScheme: "cancelScheme",
    defaultLabel: ButtonSetText.cancel,
  },
  Close: {
    icon: faDoorClosed,
    colorScheme: "closeScheme",
    defaultLabel: ButtonSetText.close,
  },
  Clear: {
    icon: faEraser,
    colorScheme: "clearScheme",
    defaultLabel: ButtonSetText.clear,
  },
  Add: {
    icon: faPlus,
    colorScheme: "addScheme",
    defaultLabel: ButtonSetText.add,
  },
  Edit: {
    icon: faPenToSquare,
    colorScheme: "editScheme",
    defaultLabel: ButtonSetText.edit,
  },
  Delete: {
    icon: faTrashCan,
    colorScheme: "deleteScheme",
    defaultLabel: ButtonSetText.delete,
  },
  Submit: {
    icon: faSquareCheck,
    colorScheme: "submitScheme",
    defaultLabel: ButtonSetText.submit,
  },
  Readmore: {
    icon: faCircleInfo,
    colorScheme: "readmoreScheme",
    defaultLabel: ButtonSetText.readmore,
  },
  Back: {
    icon: faCaretLeft,
    colorScheme: "backScheme",
    defaultLabel: ButtonSetText.back,
  },
  Next: {
    icon: faCaretRight,
    colorScheme: "nextScheme",
    defaultLabel: ButtonSetText.next,
  },
  Save: {
    icon: faSave,
    colorScheme: "saveScheme",
    defaultLabel: ButtonSetText.save,
  },
  Download: {
    icon: faDownload,
    colorScheme: "downloadScheme",
    defaultLabel: ButtonSetText.download,
  },
  Send: {
    icon: faPaperPlane,
    colorScheme: "sendScheme",
    defaultLabel: ButtonSetText.send,
  },
  ExportExcel: {
    icon: faFileExcel,
    colorScheme: "downloadScheme",
    defaultLabel: ButtonSetText.exportexcel,
  },
  ExportPdf: {
    icon: faFilePdf,
    colorScheme: "downloadScheme",
    defaultLabel: ButtonSetText.exportpdf,
  },
  ExportWord: {
    icon: faFileWord,
    colorScheme: "downloadScheme",
    defaultLabel: ButtonSetText.exportword,
  },
  Upload: {
    icon: faUpload,
    colorScheme: "uploadScheme",
    defaultLabel: ButtonSetText.upload,
  },
  View: {
    icon: faEye,
    colorScheme: "viewScheme",
    defaultLabel: ButtonSetText.view,
  },
  Print: {
    icon: faPrint,
    colorScheme: "printScheme",
    defaultLabel: ButtonSetText.print,
  },
  DeleteIcon: {
    icon: faTrashCan,
    colorScheme: "deleteScheme",
    short: true,
  },
  EditIcon: {
    icon: faPenToSquare,
    colorScheme: "editScheme",
    short: true,
  },
  ViewIcon: {
    icon: faEye,
    colorScheme: "viewScheme",
    short: true,
  },
};

// สร้างปุ่มจาก buttonConfigs
const createButton = (type) => {
  const config = buttonConfigs[type] || {};
  const Component = config.short ? ShortIconButton : IconButton;

  return ({ label, colorScheme, ...props }) => (
    <Component
      icon={config.icon}
      colorScheme={colorScheme || config.colorScheme}
      label={label || config.defaultLabel}
      {...props}
    />
  );
};

// Export ปุ่มทั้งหมด
export const Search = createButton("Search");
export const Login = createButton("Login");
export const Approved = createButton("Approved");
export const Verified = createButton("Verified");
export const Reject = createButton("Reject");
export const NotApproved = createButton("NotApproved");
export const Confirm = createButton("Confirm");
export const Cancel = createButton("Cancel");
export const Close = createButton("Close");
export const Clear = createButton("Clear");
export const Add = createButton("Add");
export const Edit = createButton("Edit");
export const Delete = createButton("Delete");
export const Submit = createButton("Submit");
export const Readmore = createButton("Readmore");
export const Back = createButton("Back");
export const Next = createButton("Next");
export const Save = createButton("Save");
export const Send = createButton("Send");
export const Download = createButton("Download");
export const ExportExcel = createButton("ExportExcel");
export const ExportPdf = createButton("ExportPdf");
export const ExportWord = createButton("ExportWord");
export const Upload = createButton("Upload");
export const View = createButton("View");
export const Print = createButton("Print");
export const DeleteIcon = createButton("DeleteIcon");
export const EditIcon = createButton("EditIcon");
export const ViewIcon = createButton("ViewIcon");

// ปุ่มพิเศษที่ไม่ใช้ buttonConfigs (ถ้ามี)
export const SidebarMenu = ({ label, icon, ...props }) => (
  <IconButton
    icon={icon}
    colorScheme="menuScheme"
    w="220px"
    size="lg"
    fontSize="13px"
    h="35px"
    label={label}
    justifyContent="left"
    {...props}
  />
);

export default IconButton;